import React, { Suspense, lazy } from 'react';
import { Routes, Route, BrowserRouter, Navigate } from 'react-router-dom';
import './App.css';
import './theme/qnaread-brand.css';
import './theme/qnaread-canvas.css';
import './theme/qnaread-modal.css';
import './responsive.css';
import Login from './components/Login';
import { AuthContextProvider } from './components/shared/AuthContext';
import AuthValidation from './components/shared/AuthValidation';
import PresenceHeartbeat from './components/shared/PresenceHeartbeat';
import MfaStepUpGate from './components/settings/MfaStepUpGate';
import AdminUsersPage from './components/pages/AdminUsersPage';
import AdminConsoleLayout from './components/admin/AdminConsoleLayout';
import AdminDashboardPage from './components/admin/AdminDashboardPage';
import AdminAdministratorControlsPage from './components/admin/AdminAdministratorControlsPage';
import { settingsManageTabUrl } from './config/settingsTabs';
import ProtectedRoute from './components/shared/ProtectedRoute';
import AdminRoute from './components/shared/AdminRoute';
import Home from './components/pages/Home';
import DashboardPage from './components/pages/DashboardPage';
import BulkUpload from './components/pages/BulkUpload';
import UserAnalytics from './components/pages/UserAnalytics';
import AddException from './components/pages/AddException';
import SavedPage from './components/pages/SavedPage';
import PracticePage from './components/pages/PracticePage';
import ReviewPage from './components/pages/ReviewPage';
import SettingsPage from './components/pages/SettingsPage';
import OnboardingPage from './components/pages/OnboardingPage';
import LandingPage from './components/pages/LandingPage';
import ProductHubPage from './components/pages/ProductHubPage';
import AuthCallbackPage from './components/pages/AuthCallbackPage';
import FeaturesPage from './components/pages/FeaturesPage';
import PricingPage from './components/pages/PricingPage';
import LegalPage from './components/pages/LegalPage';
import NotFoundPage from './components/pages/NotFoundPage';
import CheckoutPage from './components/pages/CheckoutPage';
import CheckoutSuccessPage from './components/pages/CheckoutSuccessPage';
import CheckoutCancelPage from './components/pages/CheckoutCancelPage';
import LeaderboardPage from './components/pages/LeaderboardPage';
import LearningProfilePage from './components/pages/LearningProfilePage';
import GamificationToastHost from './components/gamification/GamificationToast';
import { LearningRealtimeProvider } from './realtime/LearningRealtimeProvider';
import { UserPlanProvider } from './context/UserPlanContext';
import PlanPathGuard from './components/billing/PlanPathGuard';
import PlanProtectedRoute from './components/billing/PlanProtectedRoute';
import { FEATURE } from './config/planFeatures';
import { TeamContextProvider } from './context/TeamContext';
import { NotificationProvider } from './context/NotificationContext';
import NotificationsPage from './pages/NotificationsPage';
import TeamsLayout from './components/teams/TeamsLayout';
import TeamsHubPage from './pages/teams/TeamsHubPage';
import TeamCreatePage from './pages/teams/TeamCreatePage';
import TeamOverviewPage from './pages/teams/TeamOverviewPage';
import TeamMembersPage from './pages/teams/TeamMembersPage';
import TeamAssignmentsPage from './pages/teams/TeamAssignmentsPage';
import TeamRequestsPage from './pages/teams/TeamRequestsPage';
import TeamChallengesPage from './pages/teams/TeamChallengesPage';
import TeamLeaderboardPage from './pages/teams/TeamLeaderboardPage';
import TeamSettingsPage from './pages/teams/TeamSettingsPage';
import TeamGroupsPage from './pages/teams/TeamGroupsPage';
import TeamAuditPage from './pages/teams/TeamAuditPage';
import TeamInviteAcceptPage from './pages/teams/TeamInviteAcceptPage';
import LearnPage from './pages/learn/LearnPage';
import LearnNotebookCreatePage from './pages/learn/LearnNotebookCreatePage';
import LearnNotebookDetailPage from './pages/learn/LearnNotebookDetailPage';
import LearnImportsPage from './pages/learn/LearnImportsPage';
import LearningSettingsPage from './pages/learn/LearningSettingsPage';
import MasteryPage from './pages/adaptive/MasteryPage';
import TodayLearningPage from './pages/adaptive/TodayLearningPage';
import TutorPage from './pages/adaptive/TutorPage';
import InterviewPage from './pages/adaptive/InterviewPage';
import SkillGapPage from './pages/adaptive/SkillGapPage';
import WeeklySummaryPage from './pages/adaptive/WeeklySummaryPage';
import {
  ForStudentsPage,
  ForDevelopersPage,
  AiMarketingPage,
  ContactPage,
  SecurityMarketingPage,
} from './components/pages/MarketingPages';
import RootRedirectSeo from './components/seo/RootRedirectSeo';
import PublicQuestionPage from './pages/public/PublicQuestionPage';
import { LegacyHomeRedirect, LegacyLearnRedirect } from './routing/legacyRedirects';

const QuestionDetailPage = lazy(() => import('./components/pages/QuestionDetailPage'));

const App = () => (
  <div className="App">
    <BrowserRouter basename="/">
      <RootRedirectSeo />
      <AuthContextProvider>
        <TeamContextProvider>
        <NotificationProvider>
        <LearningRealtimeProvider>
        <UserPlanProvider>
          <AuthValidation />
          <MfaStepUpGate />
          <PresenceHeartbeat />
          <GamificationToastHost />
          <PlanPathGuard>
          <Routes>
          <Route path="/" element={<Navigate to="/product" replace />} />
          <Route path="/product" element={<ProductHubPage />} />
          <Route path="/brand" element={<LandingPage />} />
          <Route path="/q/:id" element={<PublicQuestionPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/ai" element={<AiMarketingPage />} />
          <Route path="/for-students" element={<ForStudentsPage />} />
          <Route path="/for-developers" element={<ForDevelopersPage />} />
          <Route path="/for-interview-prep" element={<ForDevelopersPage />} />
          <Route path="/for-teams" element={<PricingPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/security" element={<SecurityMarketingPage />} />
          <Route path="/privacy" element={<LegalPage variant="privacy" />} />
          <Route path="/terms" element={<LegalPage variant="terms" />} />
          <Route path="/about" element={<LegalPage variant="about" />} />
          <Route path="/cookies" element={<LegalPage variant="cookies" />} />
          <Route path="/refund" element={<LegalPage variant="refund" />} />
          <Route path="/onboarding" element={<ProtectedRoute skipOnboarding element={<OnboardingPage />} />} />
          <Route path="/hub" element={<ProtectedRoute element={<LearnPage />} />} />
          <Route path="/hub/notebooks/new" element={<ProtectedRoute element={<LearnNotebookCreatePage />} />} />
          <Route path="/hub/notebooks/:id" element={<ProtectedRoute element={<LearnNotebookDetailPage />} />} />
          <Route path="/hub/imports" element={<ProtectedRoute element={<LearnImportsPage />} />} />
          <Route path="/hub/import" element={<Navigate to="/hub" replace />} />
          <Route path="/hub/today" element={<ProtectedRoute element={<TodayLearningPage />} />} />
          <Route path="/learn/*" element={<LegacyLearnRedirect />} />
          <Route path="/mastery" element={<ProtectedRoute element={<MasteryPage />} />} />
          <Route path="/tutor" element={<ProtectedRoute element={<TutorPage />} />} />
          <Route path="/interview" element={<ProtectedRoute element={<InterviewPage />} />} />
          <Route path="/skill-gap" element={<ProtectedRoute element={<SkillGapPage />} />} />
          <Route path="/weekly-summary" element={<ProtectedRoute element={<WeeklySummaryPage />} />} />
          <Route path="/settings/learning" element={<ProtectedRoute element={<LearningSettingsPage />} />} />
          <Route path="/library" element={<ProtectedRoute element={<Home />} />} />
          <Route path="/home" element={<LegacyHomeRedirect />} />
          <Route path="/dashboard" element={<ProtectedRoute element={<DashboardPage />} />} />
          <Route path="/saved" element={<ProtectedRoute element={<SavedPage />} />} />
          <Route path="/practice" element={<ProtectedRoute element={<PracticePage />} />} />
          <Route path="/review" element={<ProtectedRoute element={<ReviewPage />} />} />
          <Route
            path="/question/:id"
            element={(
              <ProtectedRoute
                skipOnboarding
                element={(
                  <Suspense fallback={<div className="question-detail-route-fallback" aria-label="Loading question" />}>
                    <QuestionDetailPage />
                  </Suspense>
                )}
              />
            )}
          />
          <Route path="/settings" element={<ProtectedRoute element={<SettingsPage />} />} />
          <Route path="/notifications" element={<ProtectedRoute element={<NotificationsPage />} />} />
          <Route path="/checkout" element={<ProtectedRoute skipOnboarding element={<CheckoutPage />} />} />
          <Route path="/checkout/success" element={<ProtectedRoute skipOnboarding element={<CheckoutSuccessPage />} />} />
          <Route path="/checkout/cancel" element={<ProtectedRoute skipOnboarding element={<CheckoutCancelPage />} />} />
          <Route path="/analytics" element={<ProtectedRoute element={<UserAnalytics />} />} />
          <Route
            path="/leaderboard"
            element={
              <ProtectedRoute
                element={
                  <PlanProtectedRoute feature={FEATURE.LEADERBOARD_RANK} element={<LeaderboardPage />} />
                }
              />
            }
          />
          <Route path="/profile" element={<ProtectedRoute element={<LearningProfilePage />} />} />
          <Route
            path="/teams/invite/:token"
            element={<ProtectedRoute element={<TeamInviteAcceptPage />} />}
          />
          <Route path="/teams" element={<ProtectedRoute element={<TeamsLayout />} />}>
            <Route index element={<TeamsHubPage />} />
            <Route path="new" element={<TeamCreatePage />} />
            <Route path=":orgId" element={<TeamOverviewPage />} />
            <Route path=":orgId/members" element={<TeamMembersPage />} />
            <Route path=":orgId/assignments" element={<TeamAssignmentsPage />} />
            <Route path=":orgId/challenges" element={<TeamChallengesPage />} />
            <Route path=":orgId/requests" element={<TeamRequestsPage />} />
            <Route
              path=":orgId/leaderboard"
              element={
                <PlanProtectedRoute
                  feature={FEATURE.TEAM_LEADERBOARD_ANALYTICS}
                  element={<TeamLeaderboardPage />}
                />
              }
            />
            <Route path=":orgId/groups" element={<TeamGroupsPage />} />
            <Route path=":orgId/audit" element={<TeamAuditPage />} />
            <Route path=":orgId/settings" element={<TeamSettingsPage />} />
          </Route>
          <Route path="/admin/questions" element={<Navigate to={settingsManageTabUrl()} replace />} />
          <Route path="/admin" element={<AdminRoute element={<AdminConsoleLayout />} />}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="bulk-upload" element={<BulkUpload />} />
            <Route path="restrictions" element={<AddException />} />
            <Route path="system" element={<AdminAdministratorControlsPage />} />
          </Route>
          <Route path="/b_upload" element={<Navigate to="/admin/bulk-upload" replace />} />
          <Route path="/exceptions" element={<Navigate to="/admin/restrictions" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
          </PlanPathGuard>
        </UserPlanProvider>
        </LearningRealtimeProvider>
        </NotificationProvider>
        </TeamContextProvider>
      </AuthContextProvider>
    </BrowserRouter>
  </div>
);

export default App;
